// Catalogue d'aliments — liste (partagés + personnels), création/édition d'un aliment perso.
import { type FormEvent, useMemo, useState } from 'react';
import { EmptyState } from '@/components/ui';
import { energyFromMacros, type Food, round } from '@/domain';
import {
  useAddFavorite,
  useCreateFood,
  useDeleteFood,
  useFoodFavorites,
  useFoods,
  useRemoveFavorite,
  useUpdateFood,
} from '@/hooks/queries';

// Champs de composition (pour 100 g).
const FIELDS = [
  { key: 'kcal_100g', label: 'Calories (kcal)', required: true },
  { key: 'protein_100g', label: 'Protéines (g)', required: true },
  { key: 'carbs_100g', label: 'Glucides (g)', required: true },
  { key: 'sugars_100g', label: 'dont sucres (g)', required: false },
  { key: 'fat_100g', label: 'Lipides (g)', required: true },
  { key: 'saturated_100g', label: 'dont saturés (g)', required: false },
  { key: 'fiber_100g', label: 'Fibres (g)', required: false },
  { key: 'salt_100g', label: 'Sel (g)', required: false },
] as const;

type FieldKey = (typeof FIELDS)[number]['key'];

const EMPTY: Record<FieldKey, string> = {
  kcal_100g: '',
  protein_100g: '',
  carbs_100g: '',
  sugars_100g: '',
  fat_100g: '',
  saturated_100g: '',
  fiber_100g: '',
  salt_100g: '',
};

// Aliment (camelCase) → champs de formulaire (snake_case).
function toFields(f: Food): Record<FieldKey, string> {
  return {
    kcal_100g: String(f.kcal100g),
    protein_100g: String(f.protein100g),
    carbs_100g: String(f.carbs100g),
    sugars_100g: String(f.sugars100g),
    fat_100g: String(f.fat100g),
    saturated_100g: String(f.saturated100g),
    fiber_100g: String(f.fiber100g),
    salt_100g: String(f.salt100g),
  };
}

export function FoodsPage() {
  const { data: foods = [] } = useFoods();
  const { data: favorites = [] } = useFoodFavorites();
  const createFood = useCreateFood();
  const updateFood = useUpdateFood();
  const deleteFood = useDeleteFood();
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();

  const favByFood = useMemo(
    () => new Map(favorites.map((f) => [f.foodId, f.favoriteId])),
    [favorites],
  );
  const favPending = addFavorite.isPending || removeFavorite.isPending;
  function toggleFavorite(foodId: string) {
    if (favPending) return; // évite le double-clic → 2e POST rejeté par l'index unique
    const favId = favByFood.get(foodId);
    if (favId) removeFavorite.mutate(favId);
    else addFavorite.mutate(foodId);
  }

  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [values, setValues] = useState<Record<FieldKey, string>>(EMPTY);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Cohérence : kcal saisies vs reconstituées via Atwater.
  const estimatedKcal = useMemo(
    () =>
      energyFromMacros({
        protein: Number(values.protein_100g) || 0,
        carbs: Number(values.carbs_100g) || 0,
        fat: Number(values.fat_100g) || 0,
      }),
    [values.protein_100g, values.carbs_100g, values.fat_100g],
  );
  const kcalMismatch =
    Number(values.kcal_100g) > 0 && Math.abs(Number(values.kcal_100g) - estimatedKcal) > 40;

  const { publicFoods, personalFoods } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = q ? foods.filter((f) => f.name.toLowerCase().includes(q)) : foods;
    return {
      publicFoods: matches.filter((f) => f.isPublic),
      personalFoods: matches.filter((f) => !f.isPublic),
    };
  }, [foods, search]);

  function reset() {
    setEditingId(null);
    setName('');
    setValues(EMPTY);
    setError('');
  }

  function startEdit(f: Food) {
    setEditingId(f.id);
    setName(f.name);
    setValues(toFields(f));
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function remove(f: Food) {
    if (
      !window.confirm(
        `Supprimer l'aliment « ${f.name} » ? Les consommations liées seront aussi supprimées.`,
      )
    ) {
      return;
    }
    setError('');
    try {
      await deleteFood.mutateAsync(f.id);
      if (editingId === f.id) reset();
    } catch (err) {
      setError('Suppression impossible.');
      console.error(err);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const payload: Record<string, unknown> = { name };
      for (const f of FIELDS) payload[f.key] = Number(values[f.key]) || 0;
      if (editingId) await updateFood.mutateAsync({ id: editingId, values: payload });
      else await createFood.mutateAsync(payload);
      reset();
    } catch (err) {
      setError(editingId ? 'Modification impossible.' : 'Création impossible. Vérifie les champs.');
      console.error(err);
    }
  }

  const busy = createFood.isPending || updateFood.isPending;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Aliments</h1>
          <p>Catalogue partagé + tes aliments personnels (valeurs pour 100 g).</p>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 220 }}>
          <label htmlFor="food-search">Rechercher</label>
          <input
            id="food-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtrer par nom…"
          />
        </div>
      </div>

      <form className="card" onSubmit={submit} style={{ marginBottom: '1rem' }}>
        <h3>{editingId ? 'Modifier l’aliment' : 'Ajouter un aliment'}</h3>
        <div className="field" style={{ marginTop: '0.75rem' }}>
          <label htmlFor="fname">Nom</label>
          <input
            id="fname"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex. Skyr nature"
            required
            maxLength={160}
          />
        </div>
        <div className="row">
          {FIELDS.map((f) => (
            <div className="field" key={f.key}>
              <label htmlFor={f.key}>{f.label}</label>
              <input
                id={f.key}
                type="number"
                step="0.1"
                min={0}
                required={f.required}
                value={values[f.key]}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              />
            </div>
          ))}
        </div>
        {kcalMismatch && (
          <p className="muted" style={{ fontSize: '0.8rem' }}>
            ⚠️ Les calories saisies ({values.kcal_100g}) diffèrent de l'estimation Atwater (
            {round(estimatedKcal)} kcal d'après les macros). Vérifie la saisie.
          </p>
        )}
        {error && <p className="error">{error}</p>}
        <div className="toolbar" style={{ margin: 0 }}>
          <button type="submit" disabled={busy}>
            {editingId ? 'Enregistrer' : 'Ajouter'}
          </button>
          {editingId && (
            <button type="button" className="ghost" onClick={reset}>
              Annuler
            </button>
          )}
        </div>
      </form>

      <FoodTable
        title={`Mes aliments (${personalFoods.length})`}
        foods={personalFoods}
        empty={
          search
            ? `Aucun aliment personnel ne correspond à « ${search} ».`
            : "Aucun aliment personnel pour l'instant."
        }
        favByFood={favByFood}
        favPending={favPending}
        onToggleFav={toggleFavorite}
        onEdit={startEdit}
        onDelete={remove}
      />
      <div style={{ height: '1rem' }} />
      <FoodTable
        title={`Catalogue partagé (${publicFoods.length})`}
        foods={publicFoods}
        empty={
          search ? `Aucun aliment du catalogue ne correspond à « ${search} ».` : 'Catalogue vide.'
        }
        favByFood={favByFood}
        favPending={favPending}
        onToggleFav={toggleFavorite}
      />
    </>
  );
}

function FoodTable({
  title,
  foods,
  empty,
  onEdit,
  onDelete,
  favByFood,
  favPending,
  onToggleFav,
}: {
  title: string;
  foods: Food[];
  empty: string;
  favByFood: Map<string, string>;
  favPending: boolean;
  onToggleFav: (foodId: string) => void;
  onEdit?: (f: Food) => void;
  onDelete?: (f: Food) => void;
}) {
  const hasActions = Boolean(onEdit || onDelete);
  return (
    <div className="card">
      <h3>{title}</h3>
      {foods.length === 0 ? (
        <EmptyState>{empty}</EmptyState>
      ) : (
        <div className="table-wrap" style={{ marginTop: '0.5rem' }}>
          <table>
            <thead>
              <tr>
                <th aria-label="Favori"></th>
                <th>Aliment</th>
                <th>kcal</th>
                <th>Prot.</th>
                <th>Gluc.</th>
                <th>Lip.</th>
                <th>AGS</th>
                <th>Fibres</th>
                <th>Sel</th>
                {hasActions && <th></th>}
              </tr>
            </thead>
            <tbody>
              {foods.map((f) => {
                const isFav = favByFood.has(f.id);
                return (
                  <tr key={f.id}>
                    <td>
                      <button
                        type="button"
                        className="combobox-star"
                        aria-label={
                          isFav ? `Retirer ${f.name} des favoris` : `Ajouter ${f.name} aux favoris`
                        }
                        aria-pressed={isFav}
                        disabled={favPending}
                        onClick={() => onToggleFav(f.id)}
                      >
                        {isFav ? '★' : '☆'}
                      </button>
                    </td>
                    <td>{f.name}</td>
                    <td>{f.kcal100g}</td>
                    <td>{f.protein100g}</td>
                    <td>{f.carbs100g}</td>
                    <td>{f.fat100g}</td>
                    <td>{f.saturated100g}</td>
                    <td>{f.fiber100g}</td>
                    <td>{f.salt100g}</td>
                    {hasActions && (
                      <td style={{ display: 'flex', gap: '0.35rem', justifyContent: 'flex-end' }}>
                        {onEdit && (
                          <button
                            type="button"
                            className="ghost"
                            style={{ padding: '0.3rem 0.6rem', fontSize: '0.82rem' }}
                            onClick={() => onEdit(f)}
                          >
                            Modifier
                          </button>
                        )}
                        {onDelete && (
                          <button
                            type="button"
                            className="danger"
                            style={{ padding: '0.3rem 0.6rem', fontSize: '0.82rem' }}
                            onClick={() => onDelete(f)}
                          >
                            Suppr.
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
